import type { ReactNode } from "react";
import { Avatar } from "shared/components/Avatar";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  RefreshIcon,
  Cancel01Icon,
  Call02Icon,
  BubbleChatIcon,
  Chatting01Icon,
  Delete01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { AlertCircleIcon } from "@hugeicons-pro/core-solid-rounded";
import { cx } from "shared/utils";
import { isMissedCall } from "shared/utils/missed-call";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent
} from "shared/components/Tooltip";
import { IconAI } from "shared/components/ai/IconAI";
import { MessageContent } from "./MessageContent";
import { MessageTranslateButton } from "./MessageTranslateButton";
import { channelConfig } from "./constants";
import { useInboxEmbed } from "../context/useInboxEmbed";
import { useMessageTranslation } from "../hooks/useMessageTranslation";
import { shouldCollapseEmailBody } from "../utils/emailCollapse";
import {
  type Attachment,
  type NoteDetails,
  type ReplyTarget,
  type VoiceDetails
} from "../types";
import type { MessageChannel, MessageStatus, SmsKeywordType } from "../types";

interface MessageHeaderProps {
  channel: MessageChannel;
  sentBy: string;
  timestamp: string;
  noteType?: string;
  isInbound?: boolean;
  /** Voice-only (CRM-1235): the incoming call was never picked up. */
  isMissedCall?: boolean;
  autoSent?: boolean;
  translateControl?: ReactNode;
}

const TRANSLATABLE_CHANNELS: MessageChannel[] = ["sms", "chat", "email"];

const translateSitsInBubble = ({
  channel,
  isFromContact,
  receivedViaSms,
  emailCollapsible
}: {
  channel: MessageChannel;
  isFromContact: boolean;
  receivedViaSms?: boolean;
  emailCollapsible: boolean;
}): boolean => {
  if (channel === "email") return emailCollapsible;
  return channel === "chat" && isFromContact && Boolean(receivedViaSms);
};

const useMessageCardTranslation = ({
  channel,
  body,
  htmlBody,
  subject,
  isFromContact,
  receivedViaSms,
  isCompact
}: {
  channel: MessageChannel;
  body: string;
  htmlBody?: string;
  subject?: string;
  isFromContact: boolean;
  receivedViaSms?: boolean;
  isCompact: boolean;
}) => {
  const translation = useMessageTranslation({
    text: body,
    subject: channel === "email" ? subject : undefined,
    direction: isFromContact ? "inbound" : "outbound",
    enabled: TRANSLATABLE_CHANNELS.includes(channel) && Boolean(body.trim())
  });
  const translateInBubble = translateSitsInBubble({
    channel,
    isFromContact,
    receivedViaSms,
    emailCollapsible:
      isCompact && shouldCollapseEmailBody(body, htmlBody, isCompact)
  });
  const headerTranslateControl =
    translateInBubble || !translation.targetLanguage ? undefined : (
      <MessageTranslateButton translation={translation} />
    );
  return { translation, translateInBubble, headerTranslateControl };
};

const HeaderTranslateControl = ({
  control,
  dotClassName
}: {
  control?: ReactNode;
  dotClassName: string;
}) => (
  <Show if={Boolean(control)}>
    <div className={cx("size-1 rounded-full", dotClassName)} />
    {control}
  </Show>
);

// The pop-up drops the channel label and reads "From <name>" / "By <name>"
// next to the channel icon (2152:* wave brought the icon back). On the
// floating card an inbound message moves its timestamp under the bubble —
// the narrow meta row has no room for it — so it is skipped here.
const CompactMessageHeader = ({
  channel,
  sentBy,
  timestamp,
  isInbound,
  layout,
  translateControl
}: Omit<MessageHeaderProps, "noteType"> & {
  layout?: "floating" | "drawer";
}) => {
  const compactIcon =
    channel === "chat"
      ? Chatting01Icon
      : (channelConfig[channel]?.icon ?? BubbleChatIcon);
  const timeBelowBubble = layout === "floating" && isInbound;
  return (
    <div className="flex items-center gap-2 text-xs font-medium text-content-300 opacity-80">
      <HugeiconsIcon
        icon={compactIcon}
        size={20}
        className="text-content-300"
      />
      <span>{isInbound ? `From ${sentBy}` : `By ${sentBy}`}</span>
      {timeBelowBubble ? null : (
        <>
          <div className="size-1 rounded-full bg-content-100" />
          <span className="text-content-200">{timestamp}</span>
        </>
      )}
      <HeaderTranslateControl
        control={translateControl}
        dotClassName="bg-content-100"
      />
    </div>
  );
};

// Inbound AI-agent calls surface as a note; the softphone's calls use the
// voice channel in both directions (CRM-811 outbound, CRM-905 inbound). All
// render with the call icon but distinct labels, and a missed inbound call
// turns the whole row danger red (CRM-1235).
const CallMessageHeader = ({
  isOutboundCall,
  isMissed,
  sentBy,
  timestamp
}: {
  isOutboundCall: boolean;
  isMissed?: boolean;
  sentBy: string;
  timestamp: string;
}) => {
  const directionLabel = isOutboundCall ? "Outbound call" : "Incoming call";
  const toneClass = isMissed ? "text-danger-500" : "text-content-200";
  return (
    <div
      className={cx(
        "flex items-center gap-2 px-1 text-sm font-medium",
        toneClass
      )}
    >
      <HugeiconsIcon icon={Call02Icon} size={20} className={toneClass} />
      <span>{isMissed ? "Missed call" : directionLabel}</span>
      <div className="size-1 rounded-full bg-content-100" />
      <span>{isOutboundCall ? `Placed by ${sentBy}` : sentBy}</span>
      <div className="size-1 rounded-full bg-content-100" />
      <span>{timestamp}</span>
    </div>
  );
};

const MessageHeader = ({
  channel,
  sentBy,
  timestamp,
  noteType,
  isInbound,
  isMissedCall: isMissed,
  autoSent,
  translateControl
}: MessageHeaderProps) => {
  const embed = useInboxEmbed();
  const isCompact = Boolean(embed?.compact);
  const isCallTranscript = channel === "note" && noteType === "call_transcript";

  if (isCompact && !isCallTranscript && channel !== "voice")
    return (
      <CompactMessageHeader
        channel={channel}
        sentBy={sentBy}
        timestamp={timestamp}
        isInbound={isInbound}
        layout={embed?.layout}
        translateControl={translateControl}
      />
    );

  if (isCallTranscript || channel === "voice")
    return (
      <CallMessageHeader
        isOutboundCall={channel === "voice" && !isInbound}
        isMissed={isMissed}
        sentBy={sentBy}
        timestamp={timestamp}
      />
    );

  // Fall back to a safe default so an unmapped channel (e.g. a newly added
  // backend channel the client doesn't know yet) never crashes the whole inbox
  // tree — that previously unmounted SoftphoneProvider and dropped live calls.
  const { icon: channelIcon, label: channelLabel } = channelConfig[channel] ?? {
    icon: BubbleChatIcon,
    label: channel
  };
  const isManual = channel === "note";
  const actionLabel = isManual ? "Recorded by" : "Sent by";

  return (
    <div className="flex items-center gap-2 px-1 text-xs font-medium text-content-300 opacity-80">
      <HugeiconsIcon
        icon={channelIcon}
        size={16}
        className="text-content-300"
      />
      <span>{channelLabel}</span>
      <div className="size-1 rounded-full bg-content-200" />
      <span>
        {autoSent
          ? "Auto-sent by the AI leasing agent"
          : `${actionLabel} ${sentBy}`}
      </span>
      <div className="size-1 rounded-full bg-content-200" />
      <span>{timestamp}</span>
      <HeaderTranslateControl
        control={translateControl}
        dotClassName="bg-content-200"
      />
    </div>
  );
};

// "Not delivered" + retry/delete. The pop-up (design 2152:20847) stacks a
// 16px danger label over 34px buttons with a trash icon; /inbox keeps its
// original wide row.
const FailedMessageActions = ({
  compact,
  failureReason,
  retryAllowed,
  retryDisabledReason,
  onRetry,
  onDelete,
  isRetrying
}: {
  compact: boolean;
  failureReason?: string | null;
  retryAllowed?: boolean;
  retryDisabledReason?: string | null;
  onRetry?: () => void;
  onDelete?: () => void;
  isRetrying?: boolean;
}) => {
  const retryButton = (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* Disabled elements don't fire hover events — the div keeps the
            tooltip alive. */}
        <div>
          <Button
            HugeIcon={RefreshIcon}
            variant={ButtonVariant.BASE}
            size={compact ? ButtonSize.XS : ButtonSize.BASE}
            onClick={onRetry}
            disabled={isRetrying || retryAllowed === false}
          >
            {isRetrying ? "Sending..." : "Try again"}
          </Button>
        </div>
      </TooltipTrigger>
      {retryAllowed === false && retryDisabledReason && (
        <TooltipContent className="max-w-[300px] rounded-lg border bg-alpha-800 p-3 text-sm text-white z-50">
          {retryDisabledReason}
        </TooltipContent>
      )}
    </Tooltip>
  );

  const notDeliveredLabel = (
    <Tooltip>
      <TooltipTrigger>
        {compact ? (
          <div className="flex cursor-default items-center gap-2 text-sm font-bold text-danger-500">
            <HugeiconsIcon icon={AlertCircleIcon} size={16} />
            <span>Not delivered</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-red-500 cursor-default">
            <HugeiconsIcon icon={AlertCircleIcon} size={20} />
            <span className="text-base font-semibold">Not delivered</span>
          </div>
        )}
      </TooltipTrigger>
      {failureReason && (
        <TooltipContent className="max-w-[300px] rounded-lg border bg-alpha-800 p-3 text-sm text-white z-50">
          {failureReason}
        </TooltipContent>
      )}
    </Tooltip>
  );

  if (compact)
    return (
      <div className="flex w-full flex-col items-start gap-2">
        {notDeliveredLabel}
        <div className="flex items-center gap-2">
          {retryButton}
          <Button
            HugeIcon={Delete01Icon}
            variant={ButtonVariant.BASE}
            size={ButtonSize.XS}
            onClick={onDelete}
            disabled={isRetrying}
          >
            Delete
          </Button>
        </div>
      </div>
    );

  return (
    <div className="flex items-center justify-between gap-4">
      {notDeliveredLabel}
      <div className="flex items-center gap-3">
        {retryButton}
        <Button
          HugeIcon={Cancel01Icon}
          variant={ButtonVariant.BASE}
          size={ButtonSize.BASE}
          iconStrokeWidth={3}
          onClick={onDelete}
          disabled={isRetrying}
        >
          Delete
        </Button>
      </div>
    </div>
  );
};

interface MessageCardLayoutProps {
  avatar: ReactNode;
  header: ReactNode;
  isFromContact: boolean;
  children: ReactNode;
}

export const MessageCardLayout = ({
  avatar,
  header,
  isFromContact,
  children
}: MessageCardLayoutProps) => {
  const isCompact = Boolean(useInboxEmbed()?.compact);

  return (
    <div
      className={cx(
        "relative flex items-start",
        isCompact ? "gap-3" : "gap-4",
        isFromContact ? "flex-row" : "flex-row-reverse"
      )}
    >
      <div className="flex-shrink-0">{avatar}</div>

      <div
        className={cx(
          "flex w-full min-w-0 flex-col",
          isCompact ? "gap-2" : "gap-2.5",
          isFromContact ? "items-start" : "items-end"
        )}
      >
        {children}
        {header}
      </div>
    </div>
  );
};

interface MessageAddress {
  name: string;
  address: string;
}

interface MessageCardProps {
  channel: MessageChannel;
  from?: MessageAddress;
  to?: MessageAddress;
  toAddresses?: string[];
  ccAddresses?: string[];
  bccAddresses?: string[];
  subject?: string;
  isReply?: boolean;
  sentViaQuotaFallback?: boolean;
  body: string;
  htmlBody?: string;
  timestamp: string;
  sentBy: string;
  avatar: {
    initials: string;
    color: string;
  };
  /** Outbound message sent from an accepted AI suggestion — shows the AI icon instead of the sender avatar. */
  isAiGenerated?: boolean;
  /** Outbound message the AI leasing agent auto-sent — labels the header "Auto-sent by the AI leasing agent". */
  autoSent?: boolean;
  isFromContact: boolean;
  messageId?: string;
  onReply?: (target: ReplyTarget) => void;
  noteType?: string;
  noteDetails?: NoteDetails;
  voiceDetails?: VoiceDetails | null;
  attachments?: Attachment[];
  aiFlagged?: boolean;
  sentAsSms?: boolean;
  sentAsSmsTo?: string | null;
  sentAsSmsFrom?: string | null;
  smsFailedReason?: string | null;
  receivedViaSms?: boolean;
  status?: MessageStatus;
  smsKeywordType?: SmsKeywordType | null;
  failureReason?: string | null;
  retryAllowed?: boolean;
  retryDisabledReason?: string | null;
  onRetry?: () => void;
  onDelete?: () => void;
  isRetrying?: boolean;
  // Voice-only: reopen the make-a-call modal to redial a failed outbound call.
  onVoiceRetry?: () => void;
  // Voice-only: inbound call rendering (CRM-905).
  isInbound?: boolean;
}

export const MessageCard = ({
  channel,
  from,
  to,
  toAddresses,
  ccAddresses,
  bccAddresses,
  subject,
  isReply,
  sentViaQuotaFallback,
  body,
  htmlBody,
  timestamp,
  sentBy,
  avatar,
  isAiGenerated,
  autoSent,
  isFromContact,
  messageId,
  onReply,
  noteType,
  noteDetails,
  voiceDetails,
  attachments,
  aiFlagged,
  sentAsSms,
  sentAsSmsTo,
  sentAsSmsFrom,
  smsFailedReason,
  receivedViaSms,
  status,
  smsKeywordType,
  failureReason,
  retryAllowed,
  retryDisabledReason,
  onRetry,
  onDelete,
  isRetrying,
  onVoiceRetry,
  isInbound
}: MessageCardProps) => {
  // communication-service maps a no-answer/busy/failed call to Message.status
  // "failed", so without the channel guard the generic delivery-error block
  // would stack on top of the call card's own outcome line.
  const isFailed =
    channel !== "voice" && (status === "failed" || status === "bounced");
  // 32px avatars in the pop-up (design) against /inbox's 40px.
  const embed = useInboxEmbed();
  const isCompact = Boolean(embed?.compact);
  const avatarSize = isCompact ? 32 : 40;
  const isCallTranscript = channel === "note" && noteType === "call_transcript";
  // The pop-up marks a failed bubble with the design's danger border
  // (2152:20748); /inbox keeps its ring.
  const failedCardClass = isCompact
    ? "!border-danger-500"
    : "ring-2 ring-red-400";
  // Floating-card inbound messages carry their time under the bubble
  // (design 2152:9827); the meta row shows only "[icon] From <name>".
  const showTimeBelowBubble =
    isCompact &&
    embed?.layout === "floating" &&
    Boolean(isInbound) &&
    channel !== "voice" &&
    !isCallTranscript;

  // `isInbound` is deliberately undefined off the voice channel (MessagesList),
  // so the channel guard keeps a note or email from ever asking (CRM-1235).
  const isMissed =
    channel === "voice" && isMissedCall(voiceDetails, !!isInbound);

  const { translation, translateInBubble, headerTranslateControl } =
    useMessageCardTranslation({
      channel,
      body,
      htmlBody,
      subject,
      isFromContact,
      receivedViaSms,
      isCompact
    });

  return (
    <MessageCardLayout
      isFromContact={isFromContact}
      avatar={
        isAiGenerated ? (
          <IconAI bgSize={avatarSize} />
        ) : (
          <Avatar
            name={sentBy}
            size={avatarSize}
            fallbackText={avatar.initials}
            fallbackBackgroundColor={avatar.color}
            className={
              isCompact ? "!text-sm !font-bold" : "!text-base !font-black"
            }
          />
        )
      }
      header={
        <MessageHeader
          channel={channel}
          sentBy={sentBy}
          timestamp={timestamp}
          noteType={noteType}
          isInbound={isInbound}
          isMissedCall={isMissed}
          autoSent={autoSent}
          translateControl={headerTranslateControl}
        />
      }
    >
      <div
        className={cx(
          "flex w-full min-w-0 flex-col",
          isCompact ? "gap-2" : "gap-4",
          isFromContact ? "items-start" : "items-end"
        )}
      >
        <MessageContent
          channel={channel}
          isFromContact={isFromContact}
          body={body}
          subject={subject}
          isReply={isReply}
          sentViaQuotaFallback={sentViaQuotaFallback}
          from={from}
          htmlBody={htmlBody}
          to={to}
          toAddresses={toAddresses}
          ccAddresses={ccAddresses}
          bccAddresses={bccAddresses}
          messageId={messageId}
          onReply={onReply}
          noteType={noteType}
          noteDetails={noteDetails}
          attachments={attachments}
          aiFlagged={aiFlagged}
          sentAsSms={sentAsSms}
          sentAsSmsTo={sentAsSmsTo}
          sentAsSmsFrom={sentAsSmsFrom}
          smsFailedReason={smsFailedReason}
          receivedViaSms={receivedViaSms}
          smsKeywordType={smsKeywordType}
          className={isFailed ? failedCardClass : undefined}
          voiceDetails={voiceDetails}
          onVoiceRetry={onVoiceRetry}
          onDelete={onDelete}
          isInbound={isInbound}
          translation={translation}
          translateInBubble={translateInBubble}
        />

        {showTimeBelowBubble ? (
          <span className="self-end text-xs font-medium text-content-200">
            {timestamp}
          </span>
        ) : null}

        {isFailed ? (
          <FailedMessageActions
            compact={isCompact}
            failureReason={failureReason}
            retryAllowed={retryAllowed}
            retryDisabledReason={retryDisabledReason}
            onRetry={onRetry}
            onDelete={onDelete}
            isRetrying={isRetrying}
          />
        ) : null}
      </div>

      {status === "sending" && (
        <div className="text-xs text-content-200">Sending...</div>
      )}
    </MessageCardLayout>
  );
};
